import React, { useState, useRef, useEffect } from "react";
import { GoogleMap, Marker, useLoadScript, Autocomplete } from "@react-google-maps/api";
import { MapPin } from "lucide-react";

const libraries = ["places", "marker"];

const containerStyle = {
  width: "100%",
  height: "250px",
  borderRadius: "0.5rem",
};

const defaultCenter = {
  lat: 28.6139,
  lng: 77.209,
};

const LocationPicker = ({ onLocationSelect, initialLocation }) => {
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: "AIzaSyB35Pbn0txb5QfB3Hq1i04vv19tvo5xwI8",
    libraries,
  });

  const [location, setLocation] = useState(() => {
    const lat = parseFloat(initialLocation?.lat);
    const lng = parseFloat(initialLocation?.lng);
    return {
      lat: !isNaN(lat) ? lat : defaultCenter.lat,
      lng: !isNaN(lng) ? lng : defaultCenter.lng,
      address: initialLocation?.address || (typeof initialLocation === 'string' ? initialLocation : ""),
    };
  });
  
  const autocompleteRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (isLoaded && inputRef.current && (initialLocation?.address || typeof initialLocation === 'string')) {
      const addr = initialLocation?.address || (typeof initialLocation === 'string' ? initialLocation : "");
      inputRef.current.value = addr;
      
      const lat = parseFloat(initialLocation?.lat);
      const lng = parseFloat(initialLocation?.lng);
      
      if (!isNaN(lat) && !isNaN(lng)) {
        setLocation({ lat, lng, address: addr });
      }
    }
  }, [isLoaded, initialLocation]);

  if (!isLoaded) {
    return (
      <div className="w-full h-[250px] bg-white/5 rounded-lg flex items-center justify-center text-gray-400">
        Loading map...
      </div>
    );
  }

  const updateLocation = async (lat, lng, address = "") => {
    const newLoc = { lat, lng, address };
    setLocation(newLoc);
    onLocationSelect(newLoc);
  };

  const onLoad = (autocomplete) => {
    autocompleteRef.current = autocomplete;
  };

  const onPlaceChanged = () => {
    if (autocompleteRef.current !== null) {
      const place = autocompleteRef.current.getPlace();
      if (!place.geometry) return;

      const lat = place.geometry.location.lat();
      const lng = place.geometry.location.lng();
      const address = place.formatted_address || "";

      updateLocation(lat, lng, address);
    }
  };

  const handleMapClick = async (e) => {
    const lat = e.latLng.lat();
    const lng = e.latLng.lng();

    const res = await fetch(
      `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=AIzaSyB35Pbn0txb5QfB3Hq1i04vv19tvo5xwI8`
    );

    const data = await res.json();
    const address = data.results?.[0]?.formatted_address || "";

    if (inputRef.current) {
        inputRef.current.value = address;
    }
    updateLocation(lat, lng, address);
  };

  return (
    <div className="space-y-4">
      {/* Search box */}
      <div className="relative">
        <MapPin
          size={18}
          className="absolute top-1/2 left-3 -translate-y-1/2 text-gray-400 z-10"
        />

        <Autocomplete
          onLoad={onLoad}
          onPlaceChanged={onPlaceChanged}
          options={{ types: ["geocode", "establishment"] }}
        >
          <input
            type="text"
            ref={inputRef}
            className="w-full h-12 bg-black border border-white/20 rounded-lg pl-10 pr-4 text-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
            placeholder="Search for a location or address"
          />
        </Autocomplete>
      </div>

      {/* Map */}
      <div className="w-full h-[250px] rounded-lg overflow-hidden">
        <GoogleMap
          mapContainerStyle={containerStyle}
          zoom={15}
          center={{ lat: location.lat, lng: location.lng }}
          onClick={handleMapClick}
          options={{
            disableDefaultUI: true,
            zoomControl: true,
            mapId: "DEMO_MAP_ID",
          }}
        >
          <Marker position={{ lat: location.lat, lng: location.lng }} />
        </GoogleMap>
      </div>
    </div>
  );
};

export default LocationPicker;

